import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    TwoMonths,
    Recurrence,
    ControlsPageFiltersForControlsTwoMonthsPageSpecificFilters as ControlsTwoMonthsFilters,
    ControlsTwoMonthsPageSpecificFilters,
    ControlsTwoMonthsColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsTwoMonthsColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    status: null,
    recurrenceEveryTwoMonths: null,
    notification: null,
    scheduleEveryTwoMonths: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsTwoMonthsColumn[] = Object.keys(
    allColumnsGuard
) as ControlsTwoMonthsColumn[];
const encodeControlsTwoMonthsColumn: (col: string) => ControlsTwoMonthsColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsTwoMonthsSpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsTwoMonthsPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        recurrenceEveryTwoMonths: encodeOrFilter<Recurrence>(
            "recurrenceEveryTwoMonths",
            filters
        ),
        scheduleEveryTwoMonths: encodeOrFilter<TwoMonths[]>(
            "scheduleEveryTwoMonths",
            filters
        ),
    };
}
const encodeControlsTwoMonthsPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsTwoMonthsFilters = createEncodePageFiltersFn(
    encodeControlsTwoMonthsSpecificPageFilters
);

function decodeControlsTwoMonthsPageSpecificFilters(
    specificFilters: ControlsTwoMonthsPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.recurrenceEveryTwoMonths) {
        output.push(
            decodeOrFilter<Recurrence>(
                "recurrenceEveryTwoMonths",
                specificFilters.recurrenceEveryTwoMonths
            )
        );
    }
    if (specificFilters.scheduleEveryTwoMonths) {
        output.push(
            decodeOrFilter<TwoMonths[]>(
                "scheduleEveryTwoMonths",
                specificFilters.scheduleEveryTwoMonths
            )
        );
    }
    return output;
}
const decodeControlsTwoMonthsPageFilters: (
    pageFilters: ControlsTwoMonthsFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsTwoMonthsPageSpecificFilters
);

// Page state
//
type ControlsTwoMonthsPageStateWithType = PageStateData<"ControlsTwoMonths"> & {
    type: "ControlsTwoMonths";
};

function decodeControlsTwoMonthsPageState(
    pageState: ControlsTwoMonthsPageStateWithType
): PageFilterAndColumns {
    const twoMonthsPageState = unwrapPageStateAs(
        pageState,
        "ControlsTwoMonths"
    );
    const filter: SimpleAndFilter = {
        filters: decodeControlsTwoMonthsPageFilters(twoMonthsPageState.filters),
    };
    const visibleColumnIds = new Set(twoMonthsPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsTwoMonthsPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsTwoMonthsPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsTwoMonths",
        filters: encodeControlsTwoMonthsPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(
            encodeControlsTwoMonthsColumn
        ),
        version: filterAndColumns.version,
    };
}

export { encodeControlsTwoMonthsPageState, decodeControlsTwoMonthsPageState };

export type { ControlsTwoMonthsPageStateWithType };
